import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useGlobalSearch, SearchResult } from '@/hooks/useGlobalSearch';

interface GlobalSearchProps {
  isOpen: boolean;
  onClose: () => void;
}

const TYPE_CONFIG: Record<SearchResult['type'], { label: string; icon: string; color: string }> = {
  course: { label: 'Curso', icon: 'ri-play-circle-line', color: 'text-sky-400' },
  book: { label: 'Livro', icon: 'ri-book-2-line', color: 'text-[#D4AF37]' },
  webinar: { label: 'Webinar', icon: 'ri-live-line', color: 'text-green-400' },
  mentorship: { label: 'Mentoria', icon: 'ri-user-star-line', color: 'text-orange-400' },
};

const QUICK_LINKS = [
  { label: 'Cursos', href: '/cursos', icon: 'ri-play-circle-line', color: 'text-sky-400' },
  { label: 'Livros', href: '/livros', icon: 'ri-book-2-line', color: 'text-[#D4AF37]' },
  { label: 'Webinars', href: '/webinars', icon: 'ri-live-line', color: 'text-green-400' },
  { label: 'Mentoria SÍLEX', href: '/mentoria', icon: 'ri-user-star-line', color: 'text-orange-400' },
];

export default function GlobalSearch({ isOpen, onClose }: GlobalSearchProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const { query, results, loading, handleQueryChange, clearSearch } = useGlobalSearch();

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 80);
    } else {
      clearSearch();
    }
  }, [isOpen, clearSearch]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (!isOpen) onClose();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!isOpen) return null;

  const grouped = results.reduce<Record<string, SearchResult[]>>((acc, r) => {
    if (!acc[r.type]) acc[r.type] = [];
    acc[r.type].push(r);
    return acc;
  }, {});

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[10vh] px-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70"
        style={{ backdropFilter: 'blur(6px)' }}
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className="relative w-full max-w-2xl rounded-2xl overflow-hidden"
        style={{
          background: 'rgba(14,14,24,0.98)',
          border: '1px solid rgba(212,175,55,0.2)',
          boxShadow: '0 32px 80px rgba(0,0,0,0.8), 0 0 0 1px rgba(255,255,255,0.04)',
        }}
      >
        {/* Search Input */}
        <div className="flex items-center gap-3 px-5 py-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <span className="w-5 h-5 flex items-center justify-center shrink-0">
            {loading ? (
              <i className="ri-loader-4-line text-[#D4AF37] text-lg animate-spin"></i>
            ) : (
              <i className="ri-search-line text-gray-400 text-lg"></i>
            )}
          </span>
          <input
            ref={inputRef}
            type="text"
            placeholder="Buscar cursos, livros, webinars..."
            value={query}
            onChange={e => handleQueryChange(e.target.value)}
            className="flex-1 bg-transparent text-white text-base placeholder-gray-500 focus:outline-none"
          />
          {query && (
            <button
              onClick={clearSearch}
              className="w-6 h-6 flex items-center justify-center rounded-full bg-white/10 text-gray-400 hover:text-white cursor-pointer transition-colors shrink-0"
            >
              <i className="ri-close-line text-sm"></i>
            </button>
          )}
          <kbd className="hidden sm:flex items-center gap-1 px-2 py-1 bg-white/5 border border-white/15 rounded text-[10px] text-gray-500 shrink-0">
            ESC
          </kbd>
        </div>

        {/* Content */}
        <div className="max-h-[60vh] overflow-y-auto">
          {!query && (
            <div className="p-5">
              <p className="text-xs text-gray-500 uppercase tracking-widest font-semibold mb-3">Acesso Rápido</p>
              <div className="grid grid-cols-2 gap-2">
                {QUICK_LINKS.map(link => (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={onClose}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors cursor-pointer group"
                  >
                    <span className={`w-5 h-5 flex items-center justify-center ${link.color}`}>
                      <i className={`${link.icon} text-base`}></i>
                    </span>
                    <span className="text-sm text-gray-300 group-hover:text-white transition-colors">{link.label}</span>
                    <span className="ml-auto w-4 h-4 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <i className="ri-arrow-right-line text-xs text-gray-400"></i>
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {query && results.length === 0 && !loading && (
            <div className="text-center py-12 px-4">
              <span className="w-12 h-12 flex items-center justify-center rounded-full bg-white/5 mx-auto mb-4">
                <i className="ri-search-line text-2xl text-gray-500"></i>
              </span>
              <p className="text-gray-400 text-sm font-medium">Nenhum resultado para &ldquo;{query}&rdquo;</p>
              <p className="text-gray-600 text-xs mt-1">Tente palavras-chave diferentes</p>
            </div>
          )}

          {query && results.length > 0 && (
            <div className="py-3">
              {(Object.keys(grouped) as SearchResult['type'][]).map(type => {
                const cfg = TYPE_CONFIG[type];
                return (
                  <div key={type}>
                    <div className="px-5 py-2 flex items-center gap-2">
                      <span className={`w-4 h-4 flex items-center justify-center ${cfg.color}`}>
                        <i className={`${cfg.icon} text-sm`}></i>
                      </span>
                      <p className="text-[10px] text-gray-500 uppercase tracking-widest font-semibold">{cfg.label}s</p>
                    </div>
                    {grouped[type].map(result => (
                      <Link
                        key={result.id}
                        to={result.href}
                        onClick={onClose}
                        className="flex items-center gap-4 px-5 py-3 hover:bg-white/[0.04] transition-colors cursor-pointer group"
                      >
                        <div className="w-12 h-12 rounded-lg overflow-hidden shrink-0 bg-white/5">
                          <img src={result.cover} alt={result.title} className="w-full h-full object-cover" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-white leading-tight line-clamp-1 group-hover:text-[#D4AF37] transition-colors">
                            {result.title}
                          </p>
                          {result.subtitle && (
                            <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{result.subtitle}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {result.badge && (
                            <span className="px-2 py-0.5 bg-[#D4AF37]/15 border border-[#D4AF37]/30 text-[#D4AF37] text-[10px] font-semibold rounded-full whitespace-nowrap">
                              {result.badge}
                            </span>
                          )}
                          {result.price !== undefined && (
                            <span className="text-sm font-bold text-[#D4AF37] whitespace-nowrap">
                              R$ {result.price.toLocaleString('pt-BR', { minimumFractionDigits: 0 })}
                            </span>
                          )}
                          <span className="w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <i className="ri-arrow-right-line text-sm text-gray-400"></i>
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="px-5 py-3 flex items-center justify-between"
          style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
        >
          <p className="text-[10px] text-gray-600">
            {results.length > 0 ? `${results.length} resultado${results.length !== 1 ? 's' : ''}` : 'Digite para buscar'}
          </p>
          <div className="flex items-center gap-3 text-[10px] text-gray-600">
            <span className="flex items-center gap-1"><kbd className="px-1.5 py-0.5 bg-white/5 border border-white/10 rounded text-[9px]">↵</kbd> abrir</span>
            <span className="flex items-center gap-1"><kbd className="px-1.5 py-0.5 bg-white/5 border border-white/10 rounded text-[9px]">ESC</kbd> fechar</span>
          </div>
        </div>
      </div>
    </div>
  );
}
